import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { CustomerProvider } from "@/context/CustomerContext";
import AccountLogin from "@/pages/account/AccountLogin";
import AccountLayout from "@/pages/account/AccountLayout";
import MyBookings from "@/pages/account/MyBookings";
import Passport from "@/pages/account/Passport";
import Rewards from "@/pages/account/Rewards";
import Profile from "@/pages/account/Profile";
import CustomerInvoice from "@/pages/account/CustomerInvoice";
import CustomersManage from "@/pages/admin/CustomersManage";
import GiftPage from "@/pages/Gift";
import PayShare from "@/pages/PayShare";
import VouchersManage from "@/pages/admin/VouchersManage";
import Wishlist from "@/pages/Wishlist";
import Refer from "@/pages/Refer";
import PartnerPage from "@/pages/partner/PartnerPage";
import FestivePage from "@/pages/Festive";
import PartnerDashboard from "@/pages/partner/PartnerDashboard";
import PaymentResult from "@/pages/PaymentResult";
import Leaderboard from "@/pages/admin/Leaderboard";
import PaymentsLedger from "@/pages/admin/PaymentsLedger";
import ReferralsManage from "@/pages/admin/ReferralsManage";

import SiteLayout from "@/pages/SiteLayout";
import Home from "@/pages/Home";
import Trips from "@/pages/Trips";
import TripDetail from "@/pages/TripDetail";
import CalendarPage from "@/pages/CalendarPage";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Policies from "@/pages/Policies";

import AdminLogin from "@/pages/admin/AdminLogin";
import ForgotPassword from "@/pages/admin/ForgotPassword";
import ResetPassword from "@/pages/admin/ResetPassword";
import ProtectedRoute from "@/pages/admin/ProtectedRoute";
import Reports from "@/pages/admin/Reports";
import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import TripsManage from "@/pages/admin/TripsManage";
import DeparturesManage from "@/pages/admin/DeparturesManage";
import LeadsManage from "@/pages/admin/LeadsManage";
import BookingsManage from "@/pages/admin/BookingsManage";
import Analytics from "@/pages/admin/Analytics";
import Messages from "@/pages/admin/Messages";
import Campaigns from "@/pages/admin/Campaigns";
import Settings from "@/pages/admin/Settings";
import Categories from "@/pages/admin/Categories";
import ReviewsManage from "@/pages/admin/ReviewsManage";
import CouponsManage from "@/pages/admin/CouponsManage";
import AdminCalendar from "@/pages/admin/AdminCalendar";
import InvoicesManage from "@/pages/admin/InvoicesManage";
import InvoiceEditor from "@/pages/admin/InvoiceEditor";
import EmployeesManage from "@/pages/admin/EmployeesManage";
import StaffDashboard from "@/pages/admin/StaffDashboard";
import { useAuth } from "@/context/AuthContext";

function RoleHome() { const { user } = useAuth(); return user?.role === "admin" ? <Dashboard /> : <StaffDashboard />; }
function AdminOnly({ children }) { const { user } = useAuth(); return user?.role === "admin" ? children : <Navigate to="/admin" replace />; }

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <AuthProvider>
        <SettingsProvider>
          <WishlistProvider>
          <CustomerProvider>
          <BrowserRouter>
            <Routes>
              <Route element={<SiteLayout />}>
                <Route path="/" element={<Home />} />
                <Route path="/trips" element={<Trips />} />
                <Route path="/trips/:slug" element={<TripDetail />} />
                <Route path="/calendar" element={<CalendarPage />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/policies" element={<Policies />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/refer" element={<Refer />} />
                <Route path="/festive" element={<FestivePage />} />
                <Route path="/partner" element={<PartnerPage />} />
                <Route path="/partner/dashboard" element={<PartnerDashboard />} />
                <Route path="/payment/result" element={<PaymentResult />} />
                <Route path="/gift" element={<GiftPage />} />
                <Route path="/pay/:token" element={<PayShare />} />
                <Route path="/account/login" element={<AccountLogin />} />
                <Route path="/account" element={<AccountLayout />}>
                  <Route index element={<MyBookings />} />
                  <Route path="passport" element={<Passport />} />
                  <Route path="rewards" element={<Rewards />} />
                  <Route path="profile" element={<Profile />} />
                  <Route path="invoice/:id" element={<CustomerInvoice />} />
                </Route>
              </Route>

              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/forgot-password" element={<ForgotPassword />} />
              <Route path="/admin/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
                <Route index element={<RoleHome />} />
                <Route path="employees" element={<AdminOnly><EmployeesManage /></AdminOnly>} />
                <Route path="trips" element={<AdminOnly><TripsManage /></AdminOnly>} />
                <Route path="departures" element={<AdminOnly><DeparturesManage /></AdminOnly>} />
                <Route path="leads" element={<LeadsManage />} />
                <Route path="bookings" element={<BookingsManage />} />
                <Route path="analytics" element={<AdminOnly><Analytics /></AdminOnly>} />
                <Route path="reports" element={<AdminOnly><Reports /></AdminOnly>} />
                <Route path="leaderboard" element={<AdminOnly><Leaderboard /></AdminOnly>} />
                <Route path="payments" element={<AdminOnly><PaymentsLedger /></AdminOnly>} />
                <Route path="referrals" element={<AdminOnly><ReferralsManage /></AdminOnly>} />
                <Route path="customers" element={<AdminOnly><CustomersManage /></AdminOnly>} />
                <Route path="vouchers" element={<AdminOnly><VouchersManage /></AdminOnly>} />
                <Route path="messages" element={<AdminOnly><Messages /></AdminOnly>} />
                <Route path="campaigns" element={<AdminOnly><Campaigns /></AdminOnly>} />
                <Route path="settings" element={<AdminOnly><Settings /></AdminOnly>} />
                <Route path="categories" element={<AdminOnly><Categories /></AdminOnly>} />
                <Route path="reviews" element={<AdminOnly><ReviewsManage /></AdminOnly>} />
                <Route path="coupons" element={<AdminOnly><CouponsManage /></AdminOnly>} />
                <Route path="calendar" element={<AdminCalendar />} />
                <Route path="invoices" element={<InvoicesManage />} />
                <Route path="invoices/:id" element={<InvoiceEditor />} />
              </Route>
            </Routes>
          </BrowserRouter>
          <Toaster position="top-center" richColors />
          </CustomerProvider>
          </WishlistProvider>
        </SettingsProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
